import { state } from '../lib/state.js';
import { loadLineCalculatorSettings } from '../services/repository.js';
import { els } from '../app/dom.js';
import { requestRender } from '../app/events.js';
import { requireAuthenticatedSession } from '../app/auth.js';
import { setStatus, esc, localDate, normalizeClock, sectionDistanceKm } from '../lib/app-helpers.js';
import { inferredCalculatorKey, calculatorMeta } from './registry.js';
import { mytoCzPointLimitExceeded, mytoCzLimitMessage, isMytoBorderPoint, openMytoDialog, runMytoCz } from './myto-cz.js';
import { openEmytoDialog, runEmytoSk } from './emyto-sk.js';
import { openDarsgoDialog, runDarsgoSi } from './darsgo-si.js';
import { openAsfinagDialog, runAsfinagAt, normalizeAsfinagVehicleSettings } from './asfinag-at.js';
import { openHugoDialog, runHugoHu } from './hugo-hu.js';
import { openPolishConcessionDialog, runPolishConcession } from './motorways.js';
import { runEtoll } from './etoll.js';

export function renderSections(){
  els.sectionsList.textContent='';
  if(!state.currentRoute.sections.length){
    els.sectionsList.innerHTML='<div class="empty">Najpierw wyznacz trasę i granice.</div>';
    return;
  }

  state.currentRoute.sections.forEach(section=>{
    const card=document.createElement('article');
    card.className=`section-card section-row${section.isAdditionalToll?' additional-toll-card':''}`;
    const calculatorKey=section.calculatorKey||inferredCalculatorKey(section);
    card.dataset.calculator=calculatorKey||'';
    card.dataset.sectionOrder=String(section.order || '');
    const calculator=calculatorMeta(calculatorKey);
    const mytoLimitExceeded=mytoCzPointLimitExceeded(section);
    // Wynik kalkulatora musi być przypięty do konkretnej zapisanej route_version.
    // Nie uruchamiamy START dla nowej linii ani dla trasy z niezapisanymi zmianami.
    const routeReadyForCalculators=Boolean(state.currentRoute.id)&&!state.currentRoute.dirty;
    const canRun=Boolean(calculator)&&!mytoLimitExceeded&&routeReadyForCalculators;
    const distanceKm=sectionDistanceKm(section);
    const title=section.displayName||section.countryName||section.countryCode||'Odcinek';
    const routeStart=section.points?.[0]?.name||'';
    const routeEnd=section.points?.at(-1)?.name||'';
    const metaParts=[calculator?.name||'Brak kalkulatora'];
    if(distanceKm!==null)metaParts.push(`${distanceKm} km`);
    if(routeStart||routeEnd)metaParts.push(`${routeStart||'—'} → ${routeEnd||'—'}`);
    const warning=mytoLimitExceeded
      ? `<div class="section-row-warning">${esc(mytoCzLimitMessage(section))}</div>`
      : '';
    const addLabel=section.isAdditionalToll
      ? `<span class="section-row-tag">${section.countryCode==='PL'?'opłata koncesyjna':'odcinek płatny'}</span>`
      : '';
    card.innerHTML=`
      <div class="section-row-main">
        <div class="section-row-title"><strong>${esc(title)}</strong>${addLabel}</div>
        <div class="section-row-meta">${metaParts.map(esc).join(' · ')}</div>
        ${warning}
      </div>
      <div class="section-actions">
        <button class="button small section-start-button" type="button" ${canRun?'':'disabled'}>${mytoLimitExceeded?'LIMIT':'START'}</button>
      </div>`;
    if(canRun){
      card.querySelector('.section-start-button').addEventListener('click',()=>{
        if(calculatorKey==='myto-cz') openMytoDialog(section);
        else if(calculatorKey==='emyto-sk') openEmytoDialog(section);
        else if(calculatorKey==='darsgo-si') openDarsgoDialog(section);
        else if(['a1-amberone-pl','a2-awsa-pl','a4-sam-pl','hac-hr','azm-hr','bina-istra-hr','autostrade-it','autoroutes-fr'].includes(calculatorKey)) openPolishConcessionDialog(section);
        else if(calculatorKey==='asfinag-at') openAsfinagDialog(section);
        else if(calculatorKey==='hugo-hu') openHugoDialog(section);
        else runEtoll(section);
      });
    }
    els.sectionsList.appendChild(card);
  });
}


const RUN_ALL_REQUIRED_SETTINGS = {
  'myto-cz':['vehicleCategory','baseAxles','actualAxles','emissionClass','co2Class'],
  'darsgo-si':['entryCountry','exitCountry','axles','emissionClass','co2Class'],
  'asfinag-at':['vehicleCategory','emissionClass','co2Class'],
  'hugo-hu':['vehicleType','category','emissionClass','axleWeight','grossWeight','height','width','length'],
  'a1-amberone-pl':['vehicleCategory','entryName','exitName'],
  'a2-awsa-pl':['vehicleCategory','entryName','exitName'],
  'a4-sam-pl':['vehicleCategory','entryName','exitName'],
  'hac-hr':['vehicleCategory','entryName','exitName'],
  'azm-hr':['entryName','exitName'],
  'bina-istra-hr':['vehicleCategory','entryName','exitName'],
  'autostrade-it':['vehicleCategory','entryName','exitName'],
  'autoroutes-fr':['vehicleCategory','entryName','exitName'],
};

function sectionSpecificSavedSettings(saved,section){
  const orderKey=String(Number(section?.order||0));
  const sectionRoute=saved?.sectionRoutes&&typeof saved.sectionRoutes==='object'
    ? (saved.sectionRoutes[orderKey]||{})
    : {};
  return {...(saved||{}),...sectionRoute};
}

async function settingsForRunAll(section,calculatorKey){
  const now=new Date();
  if(calculatorKey==='etoll-pl')return {};
  if(calculatorKey==='emyto-sk'){
    return {
      system:'emyto-sk',
      date:localDate(now),
      time:normalizeClock(now.toTimeString().slice(0,8)),
      vehicleCategory:'112',
      emissionClass:'6',
      co2Class:'1',
    };
  }
  const saved=await loadLineCalculatorSettings(state.organizationId,state.currentRoute.lineNumber,calculatorKey)||{};
  const merged=sectionSpecificSavedSettings(saved,section);
  if(calculatorKey==='myto-cz'){
    merged.date=localDate(now);
    merged.time=normalizeClock(now.toTimeString().slice(0,8));
  }
  if(calculatorKey==='asfinag-at'){
    return normalizeAsfinagVehicleSettings(merged);
  }
  if(calculatorKey==='azm-hr'&&!merged.vehicleCategory)merged.vehicleCategory='III';
  return merged;
}

function validateRunAllSection(section,calculatorKey,settings){
  if(calculatorKey==='asfinag-at'){
    const normalized=normalizeAsfinagVehicleSettings(settings);
    if(!['bus-2','bus-3','bus-4'].includes(normalized.vehicleCategory)){
      return 'ASFINAG Austria: wybierz kategorię Autobus 2 osie, Autobus 3 osie albo Autobus 4 osie.';
    }
  }
  if(calculatorKey==='myto-cz'&&mytoCzPointLimitExceeded(section))return mytoCzLimitMessage(section);
  if(calculatorKey==='myto-cz'){
    const points=Array.isArray(section?.points)?section.points:[];
    if(isMytoBorderPoint(points[0])&&!String(settings?.entryBorderValue||'').trim()){
      return 'MYTO CZ: wybierz i zapisz przejście graniczne WJAZD.';
    }
    if(isMytoBorderPoint(points.at(-1))&&!String(settings?.exitBorderValue||'').trim()){
      return 'MYTO CZ: wybierz i zapisz przejście graniczne WYJAZD.';
    }
  }
  if(calculatorKey==='emyto-sk'&&(section?.points||[]).length>6)return 'eMyto Słowacja: za dużo punktów (maks. 6).';
  if(calculatorKey==='hugo-hu'&&(section?.points||[]).length>10)return 'HU-GO: za dużo punktów (maks. 10).';
  if(calculatorKey==='autostrade-it'&&(section?.points||[]).length>3)return 'Autostrade Italia: za dużo punktów (maks. 3).';
  if(calculatorKey==='autoroutes-fr'&&(section?.points||[]).length>5)return 'Autoroutes France: za dużo punktów (maks. 5).';
  const missing=(RUN_ALL_REQUIRED_SETTINGS[calculatorKey]||[]).filter(name=>String(settings?.[name]??'').trim()==='');
  return missing.length?`${calculatorMeta(calculatorKey)?.name||calculatorKey}: brak zapisanych parametrów (${missing.join(', ')}).`:'';
}

async function executeRunAllSection(section,calculatorKey,settings){
  if(calculatorKey==='etoll-pl')return runEtoll(section);
  if(calculatorKey==='myto-cz')return runMytoCz(section,settings);
  if(calculatorKey==='emyto-sk')return runEmytoSk(section,settings);
  if(calculatorKey==='darsgo-si')return runDarsgoSi(section,settings);
  if(calculatorKey==='asfinag-at')return runAsfinagAt(section,settings);
  if(calculatorKey==='hugo-hu')return runHugoHu(section,settings);
  if(['a1-amberone-pl','a2-awsa-pl','a4-sam-pl','hac-hr','azm-hr','bina-istra-hr','autostrade-it','autoroutes-fr'].includes(calculatorKey)){
    return runPolishConcession(section,settings);
  }
}

export async function runAllCalculatorsForCurrentLine(){
  if(!state.currentRoute?.id){
    return setStatus(els.extensionStatus,'Najpierw zapisz linię. Dopiero zapisana linia może uruchomić pełną kolejkę.','warning');
  }
  if(state.currentRoute?.dirty){
    return setStatus(els.extensionStatus,'Linia ma niezapisane zmiany. Zapisz zmiany przed uruchomieniem wszystkich kalkulatorów.','warning');
  }
  if(!await requireAuthenticatedSession('uruchomić kolejkę wszystkich kalkulatorów'))return;

  const runnable=(state.currentRoute.sections||[])
    .map(section=>({section,calculatorKey:section.calculatorKey||inferredCalculatorKey(section)}))
    .filter(item=>Boolean(calculatorMeta(item.calculatorKey)));
  if(!runnable.length)return setStatus(els.extensionStatus,'Ta linia nie ma kalkulatorów do uruchomienia.','warning');

  const prepared=[];
  const errors=[];
  for(const item of runnable){
    try{
      const settings=await settingsForRunAll(item.section,item.calculatorKey);
      const error=validateRunAllSection(item.section,item.calculatorKey,settings);
      if(error)errors.push(error);
      prepared.push({...item,settings});
    }catch(error){
      errors.push(`${calculatorMeta(item.calculatorKey)?.name||item.calculatorKey}: ${error.message}`);
    }
  }
  if(errors.length){
    const message=`Nie uruchomiono kolejki. Uzupełnij najpierw parametry:\n• ${errors.join('\n• ')}`;
    window.alert(message);
    return setStatus(els.extensionStatus,message,'warning');
  }

  const button=els.runAllCalculatorsBtn;
  if(button){button.disabled=true;button.classList.add('is-running');}
  let completed=0;
  try{
    // Oracle/e-TOLL trafia do swojej kolejki od razu; Browserless wykonujemy kolejno.
    const oracle=prepared.filter(item=>item.calculatorKey==='etoll-pl');
    const browserless=prepared.filter(item=>item.calculatorKey!=='etoll-pl');
    for(const item of oracle){
      setStatus(els.extensionStatus,`Kolejka ${completed+1}/${prepared.length}: ${calculatorMeta(item.calculatorKey)?.name||item.calculatorKey}…`,'working');
      await executeRunAllSection(item.section,item.calculatorKey,item.settings);
      completed+=1;
    }
    for(const item of browserless){
      setStatus(els.extensionStatus,`Kolejka ${completed+1}/${prepared.length}: ${calculatorMeta(item.calculatorKey)?.name||item.calculatorKey}…`,'working');
      await executeRunAllSection(item.section,item.calculatorKey,item.settings);
      completed+=1;
      await new Promise(resolve=>setTimeout(resolve,250));
    }
    setStatus(els.extensionStatus,`Kolejka linii ${state.currentRoute.lineNumber||''}: uruchomiono ${completed} kalkulatorów. Wyniki zapisują się automatycznie.`,'ok');
  }catch(error){
    console.error('[D24 RUN ALL]',error);
    setStatus(els.extensionStatus,`Kolejka zatrzymała się po ${completed} z ${prepared.length} kalkulatorów: ${error.message}`,'error');
  }finally{
    if(button){button.classList.remove('is-running');requestRender();}
  }
}

